import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  PersonRemove as PersonRemoveIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../theme/theme';
import {
  getCompanyById,
  getCompanyUsers,
  updateCompany,
  removeUserFromCompany,
  addEnterpriseAdmin,
  removeEnterpriseAdmin,
  findUserByEmail,
  inviteUserToCompany
} from '../../lib/services/enterpriseCompanyService';
import { SUBSCRIPTION_STATUS } from '../../lib/filmSubscriptionTiers';

/**
 * EnterpriseCompanyDetail Admin Page
 *
 * View and manage individual enterprise company details
 */
const EnterpriseCompanyDetail = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { currentUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    totalSeats: 10,
    billingEmail: '',
    expiresAt: ''
  });
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = await getCompanyById(companyId);
      setCompany(companyData);
      
      setFormData({
        companyName: companyData.companyName,
        totalSeats: companyData.totalSeats,
        billingEmail: companyData.billingEmail,
        expiresAt: companyData.subscriptionExpiresAt 
          ? new Date(companyData.subscriptionExpiresAt.toDate ? companyData.subscriptionExpiresAt.toDate() : companyData.subscriptionExpiresAt).toISOString().split('T')[0]
          : ''
      });
      
      const companyUsers = await getCompanyUsers(companyId);
      setUsers(companyUsers);
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError(err.message || 'Failed to load company information');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId, fetchData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSeatUtilization = () => {
    if (!company) return 0;
    return (company.usedSeats / company.totalSeats) * 100;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'warning';
    }
  };

  const handleEditClick = () => {
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      const updates = {
        companyName: formData.companyName,
        totalSeats: formData.totalSeats,
        billingEmail: formData.billingEmail
      };

      if (formData.expiresAt) {
        updates.subscriptionExpiresAt = new Date(formData.expiresAt);
      } else {
        updates.subscriptionExpiresAt = null;
      }

      await updateCompany(companyId, updates);
      setEditDialog(false);
      fetchData();
    } catch (err) {
      console.error('Error updating company:', err);
      setError(err.message || 'Failed to update company');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the company?')) {
      return;
    }

    try {
      await removeUserFromCompany(companyId, userId);
      fetchData();
    } catch (err) {
      console.error('Error removing user:', err);
      setError(err.message || 'Failed to remove user');
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      if (isAdmin) {
        await removeEnterpriseAdmin(companyId, userId);
      } else {
        await addEnterpriseAdmin(companyId, userId);
      }
      fetchData();
    } catch (err) {
      console.error('Error toggling admin:', err);
      setError(err.message || 'Failed to update admin status');
    }
  };

  const handleAddUser = async () => {
    if (!userEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setAddingUser(true);
      setError(null);

      const user = await findUserByEmail(userEmail.trim());
      if (!user) {
        setError('User not found with that email address');
        return;
      }

      if (user.enterpriseCompanyId || user.filmCompanyId) {
        setError('User is already associated with a company');
        return;
      }

      await inviteUserToCompany(companyId, user.id, currentUser.uid);
      
      setAddUserDialog(false);
      setUserEmail('');
      fetchData();
    } catch (err) {
      console.error('Error adding user:', err);
      setError(err.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error && !company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/enterprise-companies')}>
          Back to Companies
        </Button>
      </Container>
    );
  }

  if (!company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Company not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/enterprise-companies')} sx={{ mt: 2 }}>
          Back to Companies
        </Button>
      </Container>
    );
  }

  const isUserAdmin = (userId) => {
    return company.enterpriseAdminUids?.includes(userId) || false;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/admin/enterprise-companies')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Enterprise Company Details
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {company.companyName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{ bgcolor: brandColors.primary }}
        >
          Edit Company
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Company Info Card */}
      <Card elevation={3} sx={{ mb: 4, border: `2px solid ${brandColors.primary}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 48, color: brandColors.primary }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {company.companyName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={company.subscriptionStatus || 'Active'}
                  color={getStatusColor(company.subscriptionStatus)}
                  size="small"
                />
                <Chip
                  label={company.isActive ? 'Active' : 'Inactive'}
                  color={company.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EmailIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Billing Email
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {company.billingEmail || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Subscription Expires
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(company.subscriptionExpiresAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PeopleIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Active Users
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {users.length} users
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AdminIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Enterprise Admins
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {company.enterpriseAdminUids?.length || 0} admin(s)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(company.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Company ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {company.id}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Seat Usage */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Seat Utilization
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getSeatUtilization()}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: `${brandColors.primary}20`,
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getSeatUtilization() > 90 ? '#f44336' : 
                            getSeatUtilization() > 75 ? '#ff9800' : brandColors.primary
                  }
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'right', fontWeight: 600 }}>
              {company.usedSeats || 0} / {company.totalSeats || 0} seats
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {(getSeatUtilization()).toFixed(1)}% utilized
          </Typography>
          
          {getSeatUtilization() > 90 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Company is running low on seats. Consider increasing seat allocation.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Company Users ({users.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddUserDialog(true)}
              sx={{ bgcolor: brandColors.primary }}
            >
              Add User
            </Button>
          </Box>

          {users.length === 0 ? (
            <Alert severity="info">
              No users have been added to this company yet.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Added</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.displayName || user.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.filmSubscriptionTier || 'Free'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isUserAdmin(user.id) ? 'Yes' : 'No'}
                          color={isUserAdmin(user.id) ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(user.addedToEnterpriseCompanyAt || user.addedToFilmCompanyAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={isUserAdmin(user.id) ? 'Remove Admin' : 'Make Admin'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleAdmin(user.id, isUserAdmin(user.id))}
                              color={isUserAdmin(user.id) ? 'success' : 'default'}
                            >
                              <AdminIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove User">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveUser(user.id)}
                              color="error"
                            >
                              <PersonRemoveIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Billing Email"
              type="email"
              value={formData.billingEmail}
              onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Total Seats"
              type="number"
              value={formData.totalSeats}
              onChange={(e) => setFormData({ ...formData, totalSeats: parseInt(e.target.value) || 0 })}
              inputProps={{ min: company.usedSeats || 0 }}
              required
              helperText={`Cannot be less than ${company.usedSeats || 0} (currently used seats)`}
            />
            
            <TextField
              fullWidth
              label="Expires At"
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.companyName || !formData.billingEmail}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserDialog} onClose={() => setAddUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to Company</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="User Email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="user@example.com"
              helperText="Enter the email address of the user to add to this company"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserDialog(false)} disabled={addingUser}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddUser} 
            variant="contained"
            disabled={!userEmail.trim() || addingUser}
            sx={{ bgcolor: brandColors.primary }}
          >
            {addingUser ? 'Adding...' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnterpriseCompanyDetail;

